import React, { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import '@/App.css';
import Login from './pages/Login';
import Layout from './components/Layout';
import { Toaster } from 'sonner';

// Lazy load pages for faster initial load
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Customers = lazy(() => import('./pages/Customers'));
const CustomerDetail = lazy(() => import('./pages/CustomerDetail'));
const LeadDetail = lazy(() => import('./pages/LeadDetail'));
const DemoCustomers = lazy(() => import('./pages/DemoCustomers'));
const Quotes = lazy(() => import('./pages/Quotes'));
const Visits = lazy(() => import('./pages/Visits'));
const Calls = lazy(() => import('./pages/Calls'));
const CallLogs = lazy(() => import('./pages/CallLogs'));
const Sales = lazy(() => import('./pages/Sales'));
const Reports = lazy(() => import('./pages/Reports'));
const SubscriptionRenewals = lazy(() => import('./pages/SubscriptionRenewals'));
const Leads = lazy(() => import('./pages/Leads'));
const Opportunities = lazy(() => import('./pages/Opportunities'));
const Users = lazy(() => import('./pages/Users'));
const DealerSales = lazy(() => import('./pages/DealerSales'));
const DealerCommissions = lazy(() => import('./pages/DealerCommissions'));
const Partners = lazy(() => import('./pages/Partners'));
const DealerManagers = lazy(() => import('./pages/DealerManagers'));
const MyCommissions = lazy(() => import('./pages/MyCommissions'));
const Personnel = lazy(() => import('./pages/Personnel'));
const PersonnelDetail = lazy(() => import('./pages/PersonnelDetail'));
const EmployeeCommissions = lazy(() => import('./pages/EmployeeCommissions'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const Finances = lazy(() => import('./pages/Finances'));
const Agenda = lazy(() => import('./pages/Agenda'));
const DailyActivities = lazy(() => import('./pages/DailyActivities'));

// Use same-origin "/api" so the app works on ANY host it is served from
// (preview, *.emergent.host, and custom domains like progofilocrm.com).
// This avoids a hard dependency on a single baked-in backend host, which can
// cause "Network Error" when the user's browser/network can't reach that host.
const API = '/api';

// Configure axios defaults
axios.defaults.baseURL = API;

export const AuthContext = React.createContext();

// TEMPORARY DEV MODE - Remove in production
const DEV_MODE = false;

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    console.log('🚀 App mounting, DEV_MODE:', DEV_MODE);
    
    // TEMPORARY BYPASS: Auto-login in dev mode
    if (DEV_MODE) {
      const existingToken = localStorage.getItem('token');
      
      if (existingToken) {
        // Try to use existing token
        console.log('⚡ DEV MODE: Using existing token');
        axios.defaults.headers.common['Authorization'] = `Bearer ${existingToken}`;
        axios.get('/auth/me')
          .then(response => {
            console.log('✅ Token valid, user loaded:', response.data.username);
            setUser(response.data);
            setLoading(false);
          })
          .catch(() => {
            console.log('⚠️ Token invalid, performing auto-login');
            performDevLogin();
          });
      } else {
        console.log('⚡ DEV MODE: No token, performing auto-login');
        performDevLogin();
      }
      return;
    }
    
    // NORMAL AUTH FLOW (when DEV_MODE = false)
    const token = localStorage.getItem('token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      // Verify token
      axios.get('/auth/me')
        .then(response => {
          setUser(response.data);
          setLoading(false);
        })
        .catch(() => {
          localStorage.removeItem('token');
          delete axios.defaults.headers.common['Authorization'];
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
    
    async function performDevLogin() {
      try {
        const response = await axios.post('/auth/login', {
          username: 'admin',
          password: 'admin123'
        });
        
        if (response.data.access_token) {
          const token = response.data.access_token;
          const userData = response.data.user;
          
          console.log('✅ DEV MODE: Auto-login successful:', userData.username);
          localStorage.setItem('token', token);
          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          setUser(userData);
          setLoading(false);
        }
      } catch (error) {
        console.error('❌ DEV MODE: Auto-login failed:', error);
        // Fallback to normal login flow
        setLoading(false);
      }
    }
  }, []);

  const login = (token, userData) => {
    console.log('🔐 App login called with:', { 
      hasToken: !!token, 
      tokenStart: token ? token.substring(0, 20) + '...' : 'NO TOKEN',
      userUsername: userData?.username,
      userRole: userData?.role 
    });
    
    // Set token and axios header
    localStorage.setItem('token', token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    
    // Set user state AND force immediate navigation
    setUser(userData);
    setLoading(false);
    
    console.log('✅ Login process completed - forcing page reload');
    
    // Force complete page reload to ensure React picks up new state
    setTimeout(() => {
      window.location.href = '/';
    }, 100);
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-progo-50 via-white to-progo-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-progo-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  console.log('🎨 Rendering App, user:', user ? user.username : 'not logged in');

  return (
    <AuthContext.Provider value={{ user, login, logout }}>
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-progo-600"></div></div>}>
          <Routes>
            <Route 
              path="/login" 
              element={user ? <Navigate to="/" replace /> : <Login />} 
            />
            <Route 
              path="/forgot-password" 
              element={user ? <Navigate to="/" replace /> : <ForgotPassword />} 
            />
            <Route 
              path="/" 
              element={user ? <Layout /> : <Navigate to="/login" replace />}
            >
              <Route index element={<Dashboard />} />
              <Route path="agenda" element={<Agenda />} />
              <Route path="opportunities" element={<Opportunities />} />
              <Route path="leads" element={<Leads />} />
              <Route path="leads/:id" element={<LeadDetail />} />
              <Route path="demo-customers" element={<DemoCustomers />} />
              <Route path="customers" element={<Customers />} />
              <Route path="customers/:id" element={<CustomerDetail />} />
              <Route path="quotes" element={<Quotes />} />
              <Route path="visits" element={<Visits />} />
              <Route path="calls" element={<Calls />} />
              <Route path="call-logs" element={<CallLogs />} />
              <Route path="sales" element={<Sales />} />
              <Route path="reports" element={<Reports />} />
              <Route path="subscription-renewals" element={<SubscriptionRenewals />} />
              <Route path="users" element={<Users />} />
              <Route path="dealer-sales" element={<DealerSales />} />
              <Route path="dealer-commissions" element={<DealerCommissions />} />
              <Route path="employee-commissions" element={<EmployeeCommissions />} />
              <Route path="admin/employee-commissions" element={<EmployeeCommissions />} />
              <Route path="partners" element={<Partners />} />
              <Route path="my-managers" element={<DealerManagers />} />
              <Route path="my-commissions" element={<MyCommissions />} />
              <Route path="personnel" element={<Personnel />} />
              <Route path="personnel/:id" element={<PersonnelDetail />} />
              <Route path="finances" element={<Finances />} />
              <Route path="daily-activities" element={<DailyActivities />} />
            </Route>
          </Routes>
        </Suspense>
      </BrowserRouter>
    </AuthContext.Provider>
  );
}

export default App;